import { useCallback, useEffect, useState } from 'react';
import { Percent, Save } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { getAirtimeNetworkMeta } from '@/constants/airtimeNetworks';
import { useAdminTheme } from '@/context/AdminThemeContext';
import { adminAirtimeService, getApiErrorMessage } from '@/services';
import type { AirtimeNetwork, AirtimePricing } from '@/types/airtime';
import { cn } from '@/utils/cn';
import { adminShell } from '@/utils/adminTheme';
import { notify } from '@/utils/notify';

function profitPreview(faceValue: number, providerDiscount: number, roleDiscount: number) {
  const cost = faceValue * (1 - providerDiscount / 100);
  const sell = faceValue * (1 - roleDiscount / 100);
  return Math.round((sell - cost) * 100) / 100;
}

export function AdminAirtimePricingPage() {
  const { theme } = useAdminTheme();
  const shell = adminShell(theme);

  const [pricing, setPricing] = useState<AirtimePricing[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [drafts, setDrafts] = useState<Record<number, AirtimePricing>>({});

  const loadPricing = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAirtimeService.listPricing();
      const rows = res.data?.pricing ?? [];
      setPricing(rows);
      setDrafts(Object.fromEntries(rows.map((row) => [row.id, { ...row }])));
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load airtime pricing'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPricing();
  }, [loadPricing]);

  function updateDraft(id: number, patch: Partial<AirtimePricing>) {
    setDrafts((prev) => ({
      ...prev,
      [id]: { ...prev[id]!, ...patch },
    }));
  }

  async function handleSave(row: AirtimePricing) {
    const draft = drafts[row.id];
    if (!draft) return;

    if (draft.subscriberDiscount > draft.providerDiscount || draft.agentDiscount > draft.providerDiscount) {
      notify.error('Subscriber and agent discounts cannot exceed provider discount');
      return;
    }

    setSavingId(row.id);
    try {
      const res = await adminAirtimeService.updatePricing(row.id, {
        providerDiscount: draft.providerDiscount,
        subscriberDiscount: draft.subscriberDiscount,
        agentDiscount: draft.agentDiscount,
        isActive: draft.isActive,
      });
      const updated = res.data?.pricing;
      if (updated) {
        setPricing((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
        setDrafts((prev) => ({ ...prev, [updated.id]: updated }));
      }
      notify.success(`${row.network} pricing saved`);
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to save pricing'));
    } finally {
      setSavingId(null);
    }
  }

  const inputClass = cn(
    'w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2',
    shell.input,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader label="Loading airtime pricing" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold sm:text-3xl">Airtime Pricing</h1>
        <p className={cn('mt-1 text-sm sm:text-base', shell.cardMuted)}>
          Provider discount from SMEPLUG and role discounts for subscribers and agents. Profit is stored on each
          transaction.
        </p>
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        {pricing.map((row) => {
          const draft = drafts[row.id] ?? row;
          const meta = getAirtimeNetworkMeta(row.network as AirtimeNetwork);
          const subProfit = profitPreview(100, draft.providerDiscount, draft.subscriberDiscount);
          const agentProfit = profitPreview(100, draft.providerDiscount, draft.agentDiscount);

          return (
            <Card key={row.id} className={cn('p-5 sm:p-6 lg:p-7', shell.card)}>
              <div className="mb-6 flex items-center justify-between gap-4 border-b border-white/10 pb-5">
                <div className="flex items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white p-2 shadow-sm">
                    <img src={meta.logo} alt={meta.label} className="h-12 w-12 object-contain" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{row.network}</h2>
                    <p className={cn('text-sm', shell.cardMuted)}>SMEPLUG network pricing</p>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input
                    type="checkbox"
                    checked={draft.isActive}
                    onChange={(e) => updateDraft(row.id, { isActive: e.target.checked })}
                    className="h-4 w-4 rounded"
                  />
                  Active
                </label>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className={cn('mb-2 block text-xs font-semibold uppercase tracking-wide', shell.cardMuted)}>
                    Provider discount (%)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    value={draft.providerDiscount}
                    onChange={(e) => updateDraft(row.id, { providerDiscount: Number(e.target.value) })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={cn('mb-2 block text-xs font-semibold uppercase tracking-wide', shell.cardMuted)}>
                    Subscriber discount (%)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    value={draft.subscriberDiscount}
                    onChange={(e) => updateDraft(row.id, { subscriberDiscount: Number(e.target.value) })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={cn('mb-2 block text-xs font-semibold uppercase tracking-wide', shell.cardMuted)}>
                    Agent discount (%)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    value={draft.agentDiscount}
                    onChange={(e) => updateDraft(row.id, { agentDiscount: Number(e.target.value) })}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className={cn('mt-5 rounded-xl border px-4 py-3.5', shell.tableWrap)}>
                <div className="flex items-center gap-2 font-medium">
                  <Percent className="h-4 w-4 text-secondary" />
                  Profit preview on ₦100 face value
                </div>
                <p className={cn('mt-2 text-sm', shell.cardMuted)}>
                  Subscriber profit: <span className="font-semibold text-secondary">₦{subProfit.toFixed(2)}</span>
                  {' · '}
                  Agent profit: <span className="font-semibold text-secondary">₦{agentProfit.toFixed(2)}</span>
                </p>
              </div>

              <Button
                className="mt-5"
                size="lg"
                loading={savingId === row.id}
                loadingText="Saving..."
                onClick={() => void handleSave(row)}
              >
                <Save className="h-4 w-4" />
                Save {row.network}
              </Button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
